import {useCallback} from 'react';

import {useRestMutation} from '@famly/stat_apollo-query';
import type {
    ValidateBillingPlanMutationResult,
    ValidateBillingPlanMutationVariables,
} from 'web-app/api/clients/rest-types/validate-billing-plan/validate-billing-plan';

import {ValidateBillingPlan} from '../queries/validate-billing-plan.rest-mutation';
import type {MergedPlanValidationResult, PlanValidationResult} from '../types';
import {transformApiValidatedPlanToPlanValidationResult} from '../transformers/api-transformers';
import {transformPlanValidationResultsToMergedPlanValidationResults} from '../transformers/plan-validation-transformers';
import type {ApiValidatePlanInput} from '../api-types';

export const useValidatePlanBeforeSave = () => {
    const [validateBillingPlan] = useRestMutation<
        ValidateBillingPlanMutationResult,
        ValidateBillingPlanMutationVariables
    >(ValidateBillingPlan);

    const validatePlansForChildren = useCallback(
        async ({plans}: {plans: Array<ApiValidatePlanInput>}): Promise<MergedPlanValidationResult[]> => {
            const validatedPlans = plans.map(plan =>
                validateBillingPlan({
                    variables: {
                        input: {
                            plan,
                        },
                    },
                }).then((data): PlanValidationResult => {
                    if (!data.data) {
                        return {
                            type: 'server-error' as const,
                            message: 'No data returned from the server',
                            error: 500,
                        };
                    }
                    return transformApiValidatedPlanToPlanValidationResult(data.data);
                }),
            );

            const validatedPlansResults = Promise.all(validatedPlans).then(results => {
                if (results.every(result => result.type === 'success')) {
                    return results;
                }

                return transformPlanValidationResultsToMergedPlanValidationResults(results);
            });

            return validatedPlansResults;
        },
        [validateBillingPlan],
    );

    return {validatePlansForChildren};
};
